import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import {
  statusMap,
  statusOptions,
} from '@area-restrita/shared/document/document-requests';
import { DocumentRequestDto } from '@area-restrita/api-client/model/documentRequestDto';
import { getGetDocumentRequestsQueryOptions } from '@area-restrita/api-client/endpoints/document-request/document-request.gen';

import { useQueryTable } from '@/hooks/useQueryTable';
import DataGrid from '@/components/DataGrid';
import PageContainer from '@/components/layout/PageContainer';
import DataTableFacetedFilter from '@/components/DataTable/DataTableFacetedFilter';
import { Button } from '@/components/ui/button';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

const columns: ColumnDef<DocumentRequestDto>[] = [
  { id: 'id', header: 'ID', accessorKey: 'id' },
  { id: 'document', header: 'Documento', accessorKey: 'document' },
  {
    id: 'address',
    header: 'Endereço',
    accessorFn: (row) => row.reasonFields.new_address,
  },
  {
    id: 'implantationDate',
    header: 'Dt. Implantação',
    accessorFn: (row) => format(parseISO(row.implantationDate), 'dd/MM/yyyy'),
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (row) => statusMap[row.status].label,
  },
];

const CompanyDocumentRequests: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuthorization } = useAuthSessionStore();
  const canCreate = checkAuthorization(({ can }) =>
    can('create', 'document_requests'),
  );

  const canViewDetails = checkAuthorization(({ can }) =>
    can('viewDetails', 'document_requests'),
  );

  const {
    query: { isLoading },
    table,
  } = useQueryTable({
    columns,
    getQueryOptions: getGetDocumentRequestsQueryOptions,
  });

  return (
    <PageContainer title="Solicitações de alteração">
      <DataGrid
        variant="card"
        table={table}
        isLoading={isLoading}
        toolbar={
          <div className="flex items-center space-x-2">
            <DataTableFacetedFilter
              title="Status"
              options={statusOptions}
              column={table.getColumn('status')}
            />
          </div>
        }
        actions={
          canCreate && (
            <Link to="/my-document-requests/new">
              <Button size="sm">Nova Solicitação</Button>
            </Link>
          )
        }
        onRowClick={
          canViewDetails &&
          ((row: DocumentRequestDto) => {
            navigate({
              to: '/my-document-requests/$documentRequestId',
              params: { documentRequestId: row.id },
            });
          })
        }
      />
    </PageContainer>
  );
};

export default CompanyDocumentRequests;
